import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface Notificacao {
  id: string;
  tipo: string;
  titulo: string;
  mensagem: string;
  pedido_id: string | null;
  lida: boolean;
  prioridade: string;
  created_at: string;
  metadata: any;
}

export function useNotificacoesBKO() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [novasNotificacoes, setNovasNotificacoes] = useState(0);
  const [notificacoesLaterais, setNotificacoesLaterais] = useState<Notificacao[]>([]);

  const dispensarNotificacaoLateral = useCallback((id: string) => {
    setNotificacoesLaterais(prev => prev.filter(n => n.id !== id));
  }, []);

  useEffect(() => {
    if (!user) return;

    // Buscar notificações não lidas
    const buscarNotificacoes = async () => {
      const { data } = await supabase
        .from('notificacoes')
        .select('*')
        .eq('usuario_id', user.id)
        .eq('lida', false)
        .order('created_at', { ascending: false });

      if (data) {
        setNotificacoes(data);
        setNovasNotificacoes(data.length);
      }
    };

    buscarNotificacoes();

    // Subscription em tempo real
    const channel = supabase
      .channel('notificacoes-bko')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificacoes',
          filter: `usuario_id=eq.${user.id}`
        },
        (payload) => {
          const novaNotificacao = payload.new as Notificacao;
          
          // Adicionar à lista
          setNotificacoes(prev => [novaNotificacao, ...prev]);
          setNovasNotificacoes(prev => prev + 1);

          // Adicionar ao banner lateral persistente
          setNotificacoesLaterais(prev => [novaNotificacao, ...prev]);
          
          // Configurar ícone baseado no tipo
          const icones: Record<string, string> = {
            novo_pedido_contrato: '📋',
            novo_pedido_input: '🚀',
            pedido_transferido: '✅',
            leads_recebidos: '👥',
            leads_transferidos: '🔄',
            lead_ia_novo: '📡',
            lead_ia_atualizado: '🤖',
            proposta_nao_entregue: '⚠️',
          };

          const icone = icones[novaNotificacao.tipo] || '🔔';
          
          // Mostrar toast também (redundância)
          toast({
            title: `${icone} ${novaNotificacao.titulo}`,
            description: novaNotificacao.mensagem,
            duration: 15000,
          });

          // Tocar som de notificação
          try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch (e) {
            console.error('Erro ao tocar som:', e);
          }

          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: ['pedidos'] });
          queryClient.invalidateQueries({ queryKey: ['kanban'] });
          queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [user, toast, queryClient]);

  const marcarComoLida = async (id: string) => {
    await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('id', id);
    
    setNotificacoes(prev => prev.filter(n => n.id !== id));
    setNovasNotificacoes(prev => Math.max(0, prev - 1));
  };

  const marcarTodasComoLidas = async () => {
    if (!user) return;
    
    await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('usuario_id', user.id)
      .eq('lida', false);
    
    setNotificacoes([]);
    setNovasNotificacoes(0);
  };

  return { 
    notificacoes, 
    novasNotificacoes, 
    marcarComoLida,
    marcarTodasComoLidas,
    notificacoesLaterais,
    dispensarNotificacaoLateral,
  };
}
